import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchUserData, login, signOut } from './authThunk';
import { getToken, removeToken, removeRefreshToken, setToken, setRefreshToken, getUserData, removeUserData, setUserData } from '../../utils/HelperFunctions';
import api from '../../services/api';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import type { LoginFailure } from 'components/modules/auth/loginFailure';

const storedUserData = getUserData();

const initialState = {
  token: getToken() || null,
  loading: false,
  tokenData: null,
  idToken: null,
  idTokenData: null,
  user: null,
  userData: storedUserData ? JSON.parse(storedUserData) || null : null,
  error: null as string | null,
  /**
   * Why the last sign-in attempt failed, for the sign-in form to word and
   * show. Separate from `error` above -- which holds a message string from
   * other thunks -- because this one is a structured descriptor, and one field
   * holding two shapes is how a reader ends up rendering `[object Object]`.
   */
  loginError: null as LoginFailure | null,
  /**
   * Whether a sign-in attempt is in flight, for the sign-in form's pending
   * state. Separate from `loading` above, which several thunks share:
   * `fetchUserData` sets it too, has no timeout of its own, and is dispatched
   * from `MainLayout` on every mount with a token present. Borrowing it would
   * leave the sign-in form disabled behind a spinner for as long as an
   * unrelated request hangs -- the reported bug wearing a spinner.
   */
  loginInProgress: false,
  userLoading: false,
  language: '',
};

interface UserSettings {
  appLanguage?: string;
  timeFormat?: '12h' | '24h' | 'auto';
  useLocalTimezone?: boolean;
  pageSettings?: Record<string, any>;
  [key: string]: any;
}

interface User {
  password?: string; // Password is optional when fetching users
  public_key?: string; // Add public_key here as optional
  user_settings?: UserSettings;
  language?: string;
}

export const updateUser = createAsyncThunk<User, User>('auth/updateUser', async (body, { rejectWithValue }) => {
  try {
    const response = await api.put('/auth/update-user', body);
    return response.data; // Cast the response as a User object
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Error updating user'));
  }
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      const { token, refreshToken, userData } = action.payload;
      state.token = token;
      // state.userData = userData;
      state.loading = false;
      state.error = null;
      // An SSO success clears a previous password failure. `loginError`
      // outlives the sign-in page, and only `login.pending` clears it --
      // which is after the user has already read a message about an attempt
      // they have since abandoned.
      state.loginError = null;
      setToken(token);
      if (refreshToken) {
        setRefreshToken(refreshToken);
      }
      // setUserData(JSON.stringify(userData));
    },
    // Optimistically merge a single user_settings field into userData so the
    // change is reflected before the updateUser round-trip resolves. The
    // subsequent updateUser.fulfilled replaces userData with the server's
    // merged copy, so this is a presentation-only head start, not the source
    // of persistence.
    setUserSetting: (state, action: PayloadAction<{ key: keyof UserSettings; value: unknown }>) => {
      const { key, value } = action.payload;
      if (!state.userData) state.userData = {};
      state.userData.user_settings = {
        ...(state.userData.user_settings || {}),
        [key]: value,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signOut.fulfilled, (state) => {
        removeToken();
        removeRefreshToken();
        state.loading = false;
        state.userData = {};
        state.token = null;
        state.tokenData = null;
        state.idToken = null;
        state.idTokenData = null;
        state.error = null;
        // Do not greet the next sign-in with the last one's failure.
        state.loginError = null;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.loginInProgress = true;
        state.loginError = null;
        state.token = null;
        state.tokenData = null;
        state.idToken = null;
        state.idTokenData = null;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loginInProgress = false;
        // Only the token. `tokenData`, `idToken` and `idTokenData` are not in
        // this response and never were -- they belong to the SSO path and
        // arrive through `loginSuccess` -- so assigning them here only ever
        // wrote `undefined` over the nulls `login.pending` had just set.
        state.token = action.payload.token;
        state.loading = false;
        state.error = null;
        state.loginError = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.loginInProgress = false;
        // `payload` is the sanitised descriptor the thunk rejected with; it is
        // absent only if the thunk threw outside its own catch, and an
        // unexplained failure still has to reach the user as something.
        state.loginError = action.payload ?? { status: undefined, code: undefined };
      })
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        const { ...userData } = action.payload;
        // state.token = token;
        // state.tokenData = tokenData;
        // state.idToken = idToken;
        // state.idTokenData = idTokenData;
        state.userData = userData;
        state.loading = false;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.loading = false;
        state.userData = {};
        state.token = null;
        state.tokenData = null;
        state.idToken = null;
        state.idTokenData = null;
        state.error = action.error.message ?? null;
      })
      .addCase(updateUser.pending, (state) => {
        state.userLoading = true;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.userData = action.payload;
        state.userLoading = false;
      })
      .addCase(updateUser.rejected, (state) => {
        state.userLoading = false;
      });
  },
});

export const { loginSuccess, setUserSetting } = authSlice.actions;

export default authSlice.reducer;
